import { RequestService } from '@tcc-online/request-service';
import Participant360 from '../types/project/360/Participant360';
import Evaluator from '../types/project/360/Evaluator';
import { isSuccessfulResponse } from '../../helpers';
import ImportService from './ImportService';
import type { ParticipantImportPreviewResponse } from '../types/ParticipantImportPreviewResponse';

const Admin360ParticipantService = {
    getParticipants,
    getParticipant,
    createParticipant,
    deleteParticipant,
    getEvaluators,
    getEvaluator,
    createEvaluator,
    editEvaluator,
    deleteEvaluator,
    batchDeleteEvaluators,
    deleteAllEvaluators,
    importParticipants,
    getImportTemplate,
    exportParticipants,
};

const createUrl = (projectId: number, participantId?: number) => {
    let url = `/admin/products/360/projects/${projectId}/participants`;
    if (participantId) {
        url += `/${participantId}`;
    }

    return url;
};

export interface BasePayload {
    projectId: number;
}

async function getParticipants(payload: BasePayload) {
    return await RequestService.get<Participant360[]>(
        createUrl(payload.projectId),
    );
}

export interface ParticipantPayload extends BasePayload {
    participantId: number;
}

async function getParticipant(payload: ParticipantPayload) {
    const { projectId, participantId } = payload;
    return await RequestService.get<Participant360>(
        createUrl(projectId, participantId),
    );
}

export interface CreatePayload {
    email?: string;
    id?: number;
    firstName?: string;
    lastName?: string;
}

async function createParticipant(payload: CreatePayload & BasePayload) {
    const { projectId, ...body } = payload;
    return await RequestService.post<CreatePayload, Participant360>(
        createUrl(projectId),
        body,
    );
}

async function deleteParticipant(payload: ParticipantPayload) {
    const { projectId, participantId } = payload;
    return await RequestService.delete(createUrl(projectId, participantId));
}

export interface ImportParticipantsPayload extends BasePayload {
    data: FormData;
}

export interface ImportParticipantsResponse {
    importId: number;
    preview: ParticipantImportPreviewResponse;
}

async function importParticipants(payload: ImportParticipantsPayload) {
    const { projectId, data } = payload;
    const result = await RequestService.post<
        FormData,
        ImportParticipantsResponse
    >(`${createUrl(projectId)}/import`, data, {
        headers: { 'Content-Type': 'multipart/form-data' },
    });

    if (isSuccessfulResponse(result.status) && result.data) {
        ImportService.saveImportId(result.data.importId);
    }

    return result;
}

async function getImportTemplate(payload: BasePayload) {
    return await RequestService.get<Blob>(
        `${createUrl(payload.projectId)}/import/template`,
        {},
        { responseType: 'blob' },
    );
}

async function exportParticipants(payload: BasePayload) {
    return await RequestService.get<Blob>(
        `${createUrl(payload.projectId)}/export`,
        {},
        { responseType: 'blob' },
    );
}

const createEvaluatorUrl = (
    projectId: number,
    participantId: number,
    evaluatorId?: number,
) => {
    let url = `${createUrl(projectId, participantId)}/evaluators`;
    if (evaluatorId) {
        url += `/${evaluatorId}`;
    }

    return url;
};

async function getEvaluators(payload: ParticipantPayload) {
    return await RequestService.get<Evaluator[]>(
        createEvaluatorUrl(payload.projectId, payload.participantId),
    );
}

export interface EvaluatorPayload extends ParticipantPayload {
    evaluatorId: number;
}

async function getEvaluator(payload: EvaluatorPayload) {
    const { projectId, participantId, evaluatorId } = payload;
    return await RequestService.get<Evaluator>(
        createEvaluatorUrl(projectId, participantId, evaluatorId),
    );
}

export interface CreateEvaluatorPayload {
    email?: string;
    id?: number;
    relationship: string;
}

async function createEvaluator(
    payload: CreateEvaluatorPayload & ParticipantPayload,
) {
    const { projectId, participantId, email, id, relationship } = payload;
    return await RequestService.post<CreateEvaluatorPayload, Evaluator>(
        createEvaluatorUrl(projectId, participantId),
        { email, id, relationship },
    );
}

export interface EditEvaluatorPayload {
    relationship: string;
}

async function editEvaluator(payload: EditEvaluatorPayload & EvaluatorPayload) {
    const { projectId, participantId, evaluatorId, relationship } = payload;
    return await RequestService.patch<EditEvaluatorPayload, Evaluator>(
        createEvaluatorUrl(projectId, participantId, evaluatorId),
        {
            relationship,
        },
    );
}

async function deleteEvaluator(payload: EvaluatorPayload) {
    const { projectId, participantId, evaluatorId } = payload;
    return await RequestService.delete(
        createEvaluatorUrl(projectId, participantId, evaluatorId),
    );
}

export interface BatchDeleteEvaluatorsPayload {
    projectId: number;
    participantId: number;
    ids: number[];
}

async function batchDeleteEvaluators(payload: BatchDeleteEvaluatorsPayload) {
    const { projectId, participantId, ids } = payload;
    return await RequestService.post<{ ids: number[] }, unknown>(
        `${createEvaluatorUrl(projectId, participantId)}/batch-delete`,
        { ids },
    );
}

export interface DeleteAllEvaluatorsPayload {
    projectId: number;
    participantId: number;
}

async function deleteAllEvaluators(payload: DeleteAllEvaluatorsPayload) {
    const { projectId, participantId } = payload;
    return await RequestService.delete(
        createEvaluatorUrl(projectId, participantId),
    );
}

export default Admin360ParticipantService;
